import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type WizardStep = "profile" | "objectives" | "availability" | "review";

interface StepDef {
  id: WizardStep;
  label: string;
  shortLabel: string;
}

const STEPS: StepDef[] = [
  { id: "profile", label: "Perfil de saúde", shortLabel: "Perfil" },
  { id: "objectives", label: "Objetivos", shortLabel: "Objetivos" },
  { id: "availability", label: "Disponibilidade", shortLabel: "Tempo" },
];

interface TrainingPlanStepperProps {
  currentStep: WizardStep;
  onStepClick?: (step: WizardStep) => void;
}

export function TrainingPlanStepper({ currentStep, onStepClick }: TrainingPlanStepperProps) {
  if (currentStep === "review") return null;
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {STEPS.map((step, index) => {
          const isActive = index === currentIndex;
          const isComplete = index < currentIndex;
          const isClickable = !!onStepClick && index <= currentIndex;

          return (
            <div key={step.id} className="flex items-center flex-1 min-w-0">
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(step.id)}
                className={cn(
                  "flex items-center gap-2 sm:gap-3 min-w-0 group",
                  isClickable && "cursor-pointer",
                  !isClickable && "cursor-default",
                )}
              >
                <div
                  className={cn(
                    "flex items-center justify-center size-8 sm:size-9 rounded-full text-sm font-semibold transition-colors shrink-0 border",
                    isActive && "bg-primary text-primary-foreground border-primary",
                    isComplete && "bg-primary/10 text-primary border-primary/30",
                    !isActive && !isComplete && "bg-muted text-muted-foreground border-border",
                  )}
                >
                  {isComplete ? <Check className="size-4" /> : index + 1}
                </div>
                <div className="hidden sm:flex flex-col text-left min-w-0">
                  <span
                    className={cn(
                      "text-sm font-medium truncate",
                      isActive && "text-foreground",
                      !isActive && "text-muted-foreground",
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                <span
                  className={cn(
                    "sm:hidden text-xs font-medium truncate",
                    isActive ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {step.shortLabel}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-px mx-2 sm:mx-4",
                    index < currentIndex ? "bg-primary/40" : "bg-border",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
